/**
 * Indian Rummy meld logic: pure sequences, impure sequences (wild jokers filling in), sets, and a solver that finds the
 * arrangement of a hand with the fewest leftover points. Used by the engine to validate declarations and score the
 * other players' hands, and by bots and the UI to arrange a hand.
 *
 * Definitions (13-card Points Rummy, two decks plus printed jokers):
 *  - Wild jokers: every printed joker and every card of the "wild rank" (the rank of the cut joker). A wild may stand
 *    in for any card in a sequence or set.
 *  - Pure sequence: three or more consecutive cards of one suit with no printed joker and no wild standing in. A card
 *    of the wild rank sitting in its natural place counts as natural. Ace is low (A-2-3) or high (Q-K-A), never both.
 *  - Sequence: three or more consecutive same-suit cards, wilds allowed.
 *  - Set: three or four cards of one rank in different suits, wilds allowed.
 *  - A hand is "made" when every card is in a group, with at least one pure sequence and at least two sequences.
 *  - A losing hand counts its leftover cards (A/K/Q/J/10 = 10, others their pip value, wilds 0), capped. With no pure
 *    sequence every card counts; with a pure sequence but no second sequence only the pure sequence is safe.
 */
import { parseCard, type CardId } from '../../core/cards.js';

export type RummyMeldKind = 'pure' | 'seq' | 'set';

export interface RummyMeld {
  mask: number;
  kind: RummyMeldKind;
  points: number;
}

export interface RummyArrangement {
  /** Groups in display order: pure sequences, other sequences, then sets. */
  groups: CardId[][];
  /** Cards left over, in the order given. */
  rest: CardId[];
  /** Points of the leftover cards under the scoring rules above (uncapped). */
  deadwood: number;
}

const INF = 30000;

export const isRummyWild = (id: CardId, wildRank: number): boolean => {
  const c = parseCard(id);
  return c.joker || c.rank === wildRank;
};

/** Penalty value of a card when it is not part of a group. */
export function rummyCardPoints(id: CardId, wildRank: number): number {
  const c = parseCard(id);
  if (c.joker || c.rank === wildRank) return 0;
  return c.rank >= 10 ? 10 : c.rank;
}

function popcount(x: number): number {
  let n = 0;
  while (x) {
    x &= x - 1;
    n++;
  }
  return n;
}

const ctz = (x: number): number => 31 - Math.clz32(x & -x);

/** Do these ranks (distinct) fit in a run of `len` positions, with the ace either low or high? */
function fits(ranks: number[], len: number): boolean {
  let seen = 0;
  for (const r of ranks) {
    if (seen & (1 << r)) return false;
    seen |= 1 << r;
  }
  const span = (rs: number[]) => Math.max(...rs) - Math.min(...rs) + 1;
  if (span(ranks) <= len) return true;
  return ranks.includes(14) && span(ranks.map((r) => (r === 14 ? 1 : r))) <= len;
}

/** Exactly consecutive (no gaps) with the ace low or high. */
function consecutive(ranks: number[]): boolean {
  const n = ranks.length;
  const ok = (rs: number[]) => new Set(rs).size === n && Math.max(...rs) - Math.min(...rs) + 1 === n;
  return ok(ranks) || (ranks.includes(14) && ok(ranks.map((r) => (r === 14 ? 1 : r))));
}

export function isRummyPureSequence(cards: readonly CardId[]): boolean {
  if (cards.length < 3) return false;
  const cs = cards.map(parseCard);
  if (cs.some((c) => c.joker)) return false;
  if (!cs.every((c) => c.suit === cs[0]!.suit)) return false;
  return consecutive(cs.map((c) => c.rank));
}

/** Any valid sequence (wilds allowed, at least one natural card). */
export function isRummySequence(cards: readonly CardId[], wildRank: number): boolean {
  if (cards.length < 3 || cards.length > 14) return false;
  const nat = cards.filter((c) => !isRummyWild(c, wildRank)).map(parseCard);
  if (nat.length === 0) return false;
  if (!nat.every((c) => c.suit === nat[0]!.suit)) return false;
  return fits(nat.map((c) => c.rank), cards.length);
}

export function isRummySet(cards: readonly CardId[], wildRank: number): boolean {
  if (cards.length < 3 || cards.length > 4) return false;
  const nat = cards.filter((c) => !isRummyWild(c, wildRank)).map(parseCard);
  if (!nat.every((c) => c.rank === nat[0]!.rank)) return false;
  return new Set(nat.map((c) => c.suit)).size === nat.length;
}

/** Every valid group (as a bitmask over card indices) in a hand of up to 16 cards. */
export function rummyMelds(cards: readonly CardId[], wildRank: number): RummyMeld[] {
  const n = cards.length;
  if (n > 16) throw new Error('rummyMelds supports at most 16 cards');
  const cs = cards.map(parseCard);
  const wild = cs.map((c) => c.joker || c.rank === wildRank);
  const pts = cards.map((c) => rummyCardPoints(c, wildRank));
  let wildMask = 0;
  let printedMask = 0;
  const suitMask = [0, 0, 0, 0];
  const naturalSuitMask = [0, 0, 0, 0];
  const rankMask = new Map<number, number>();
  const SUIT_INDEX: Record<string, number> = { S: 0, H: 1, C: 2, D: 3 };
  cs.forEach((c, i) => {
    const bit = 1 << i;
    if (wild[i]) wildMask |= bit;
    if (c.joker) {
      printedMask |= bit;
      return;
    }
    suitMask[SUIT_INDEX[c.suit]!]! |= bit;
    if (!wild[i]) {
      naturalSuitMask[SUIT_INDEX[c.suit]!]! |= bit;
      rankMask.set(c.rank, (rankMask.get(c.rank) ?? 0) | bit);
    }
  });
  const out: RummyMeld[] = [];
  const full = (1 << n) - 1;
  for (let mask = 7; mask <= full; mask++) {
    const size = popcount(mask);
    if (size < 3) continue;
    const nat = mask & ~wildMask;
    let points = 0;
    for (let m = mask; m; m &= m - 1) points += pts[ctz(m)]!;
    // Pure: no printed joker, one suit (wild-rank cards count as themselves), consecutive.
    if (!(mask & printedMask)) {
      const first = ctz(mask);
      const s = SUIT_INDEX[cs[first]!.suit]!;
      if ((mask & ~suitMask[s]!) === 0) {
        const ranks: number[] = [];
        for (let m = mask; m; m &= m - 1) ranks.push(cs[ctz(m)]!.rank);
        if (consecutive(ranks)) {
          out.push({ mask, kind: 'pure', points });
          continue;
        }
      }
    }
    if (nat === 0) {
      out.push({ mask, kind: 'set', points });
      continue;
    }
    const idx = ctz(nat);
    const s = SUIT_INDEX[cs[idx]!.suit]!;
    if ((nat & ~naturalSuitMask[s]!) === 0) {
      const ranks: number[] = [];
      for (let m = nat; m; m &= m - 1) ranks.push(cs[ctz(m)]!.rank);
      if (fits(ranks, size)) {
        out.push({ mask, kind: 'seq', points });
        continue;
      }
    }
    if (size <= 4 && (nat & ~(rankMask.get(cs[idx]!.rank) ?? 0)) === 0) {
      const suits = new Set<string>();
      let dup = false;
      for (let m = nat; m; m &= m - 1) {
        const su = cs[ctz(m)]!.suit;
        if (suits.has(su)) dup = true;
        suits.add(su);
      }
      if (!dup) out.push({ mask, kind: 'set', points });
    }
  }
  return out;
}

/** Solver over one hand; `deadwood(exclude)` scores the hand with one card (the discard) removed. */
export class RummyAnalysis {
  readonly cards: CardId[];
  private readonly pts: number[];
  private readonly melds: RummyMeld[];
  private readonly byLow: RummyMeld[][];
  private readonly n: number;
  private readonly full: number;
  private readonly memo: Int16Array;
  private readonly softMemo: Int16Array;

  constructor(cards: readonly CardId[], readonly wildRank: number) {
    this.cards = [...cards];
    this.n = cards.length;
    this.full = (1 << this.n) - 1;
    this.pts = cards.map((c) => rummyCardPoints(c, wildRank));
    this.melds = rummyMelds(cards, wildRank);
    this.byLow = Array.from({ length: this.n }, () => []);
    for (const m of this.melds) this.byLow[ctz(m.mask)]!.push(m);
    this.memo = new Int16Array((1 << this.n) * 6).fill(-1);
    this.softMemo = new Int16Array((1 << this.n) * 6).fill(-1);
  }

  private static status(st: number, kind: RummyMeldKind): number {
    const pure = st >= 3 ? 1 : 0;
    let seqs = st % 3;
    if (kind !== 'set') seqs = Math.min(2, seqs + 1);
    return (kind === 'pure' || pure ? 3 : 0) + seqs;
  }

  /** Minimum leftover points when every card must be grouped (INF if no full arrangement has a pure + 2 sequences). */
  private solve(used: number, st: number): number {
    if (used === this.full) return st === 5 ? 0 : INF;
    const key = used * 6 + st;
    const hit = this.memo[key]!;
    if (hit >= 0) return hit;
    const i = ctz(~used & this.full);
    let best = this.pts[i]! + this.solve(used | (1 << i), st);
    for (const m of this.byLow[i]!) {
      if (m.mask & used) continue;
      const v = this.solve(used | m.mask, RummyAnalysis.status(st, m.kind));
      if (v < best) best = v;
    }
    best = Math.min(best, INF);
    this.memo[key] = best;
    return best;
  }

  private total(exclude: number): number {
    let t = 0;
    for (let i = 0; i < this.n; i++) if (i !== exclude) t += this.pts[i]!;
    return t;
  }

  private bestPure(exclude: number): RummyMeld | null {
    let best: RummyMeld | null = null;
    const bit = exclude >= 0 ? 1 << exclude : 0;
    for (const m of this.melds) if (m.kind === 'pure' && !(m.mask & bit) && (!best || m.points > best.points)) best = m;
    return best;
  }

  /** Leftover points of the hand with card `exclude` (an index, or -1 for none) set aside. Uncapped. */
  deadwood(exclude = -1): number {
    const start = exclude >= 0 ? 1 << exclude : 0;
    const total = this.total(exclude);
    const a = this.solve(start, 0);
    const pure = this.bestPure(exclude);
    const b = pure ? total - pure.points : INF;
    return Math.min(a, b, total);
  }

  /** Extra cost, in points, for still missing a pure sequence / a second sequence (indexed by the solver status). */
  private static readonly MISSING = [40, 32, 24, 0, 12, 0];

  private soft(used: number, st: number): number {
    if (used === this.full) return RummyAnalysis.MISSING[st]!;
    const key = used * 6 + st;
    const hit = this.softMemo[key]!;
    if (hit >= 0) return hit;
    const i = ctz(~used & this.full);
    let best = this.pts[i]! + this.soft(used | (1 << i), st);
    for (const m of this.byLow[i]!) {
      if (m.mask & used) continue;
      const v = this.soft(used | m.mask, RummyAnalysis.status(st, m.kind));
      if (v < best) best = v;
    }
    this.softMemo[key] = best;
    return best;
  }

  /**
   * A smoother "how far from winning" measure for bots: leftover points plus a penalty for each requirement (pure
   * sequence, second sequence) the best grouping still lacks. 0 means a complete winning hand.
   */
  softScore(exclude = -1): number {
    return this.soft(exclude >= 0 ? 1 << exclude : 0, 0);
  }

  /** True when discarding some single card leaves a complete winning hand. */
  isMadeAny(): boolean {
    for (let i = 0; i < this.n; i++) if (this.isMade(i)) return true;
    return false;
  }

  /** True when the hand (minus `exclude`) is a complete winning hand. */
  isMade(exclude = -1): boolean {
    return this.solve(exclude >= 0 ? 1 << exclude : 0, 0) === 0;
  }

  /** The groups behind {@link deadwood}. */
  arrangement(exclude = -1): RummyArrangement {
    const start = exclude >= 0 ? 1 << exclude : 0;
    const total = this.total(exclude);
    const a = this.solve(start, 0);
    const pure = this.bestPure(exclude);
    const b = pure ? total - pure.points : INF;
    const chosen: RummyMeld[] = [];
    let deadwood = total;
    if (a <= b && a < total) {
      let used = start;
      let st = 0;
      while (used !== this.full) {
        const i = ctz(~used & this.full);
        const here = this.solve(used, st);
        let moved = false;
        for (const m of this.byLow[i]!) {
          if (m.mask & used) continue;
          const nst = RummyAnalysis.status(st, m.kind);
          if (this.solve(used | m.mask, nst) === here) {
            chosen.push(m);
            used |= m.mask;
            st = nst;
            moved = true;
            break;
          }
        }
        if (!moved) used |= 1 << i;
      }
      deadwood = a;
    } else if (pure && b < total) {
      chosen.push(pure);
      deadwood = b;
    }
    const order: Record<RummyMeldKind, number> = { pure: 0, seq: 1, set: 2 };
    chosen.sort((x, y) => order[x.kind] - order[y.kind]);
    const inGroup = new Set<number>();
    const groups = chosen.map((m) => {
      const g: CardId[] = [];
      for (let i = 0; i < this.n; i++)
        if (m.mask & (1 << i)) {
          g.push(this.cards[i]!);
          inGroup.add(i);
        }
      return g;
    });
    const rest = this.cards.filter((_, i) => i !== exclude && !inGroup.has(i));
    return { groups, rest, deadwood };
  }
}

export const analyzeRummyHand = (cards: readonly CardId[], wildRank: number): RummyAnalysis => new RummyAnalysis(cards, wildRank);
